import { Box } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { WEEKDAYS } from "@/pages/Settings/DepartmentConfiguration/utils";
import { LOCATION_STAFFING as S, STATUS, STEPPER } from "@/theme/tokens";
import { DayToggleGrid } from "@/ui/Clickable/DayToggleGrid";
import { TrashLineIcon } from "@/ui/Icons/TrashLineIcon";
import { SystemStepper } from "@/ui/Inputs/SystemStepper";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { V2_PANEL_HAIRLINE, V2_STAFF_COUNT_MAX } from "./consts";
import { toggleWeekday, type WeekdayChip } from "./dayToggleCells";
import type { DayAmountRow } from "./locationConfigData";
import { formatLadderDays } from "./ratioLadderCoverage";
import { StaffingFieldV2 } from "./StaffingFieldV2";
import type { StaffingTargetRowsCommonProps } from "./StaffingTargetRows";

type Props = Pick<StaffingTargetRowsCommonProps, "onUpdateRow" | "onRemoveRow"> & {
	row: DayAmountRow;
	/** The week's days in the department's order, with one-letter glyphs. */
	chips: WeekdayChip[];
	/** Days another group already has: still selectable here, but a chip picked in both is marked as an overlap. */
	heldElsewhere: string[];
	/** The only group, or one whose days have shifts: it can't be removed. */
	cannotRemove: boolean;
	/** Its days have shifts, which is why it can't be removed. */
	hasOccupiedDays: boolean;
	/** Read-only: every control shows its value but takes no input. */
	disabled: boolean;
};

/**
 * One day group of the v2 staffing targets, on one line where it fits: its days as round chips, then Target and Required
 * as compact steppers under their labels with its remove right after them, so the line reads as one compact unit (as a
 * ratio step does) instead of leaving its remove stranded at the panel's far edge. Where the line is too narrow, the
 * amounts and the remove wrap together under the days. Lines after the first are parted by a hairline. A day this group
 * shares with another carries a red corner mark, as a ratio step's conflicting day does, until one of them drops it.
 */
export const StaffingTargetLineV2: FC<Props> = ({
	row,
	chips,
	heldElsewhere,
	cannotRemove,
	hasOccupiedDays,
	onUpdateRow,
	onRemoveRow,
	disabled,
}): ReactElement => {
	const { t } = useTranslation();
	const overlapMarker = { color: STATUS.ERROR.main, tooltip: t("schedule.template.editor.targets.dayOverlap") };
	return (
		<Box
			role="group"
			aria-label={row.days.length > 0 ? formatLadderDays(row.days) : t("schedule.template.editor.targets.noDays")}
			data-testid={`location-staffing-target-row-${row.id}`}
			sx={{
				display: "flex",
				flexWrap: "wrap",
				alignItems: "flex-end",
				columnGap: `${S.LINE_GAP_X}px`,
				rowGap: `${S.LINE_GAP_Y}px`,
				pb: `${S.LINE_PAD_Y}px`,
				"& + &": { pt: `${S.LINE_PAD_Y}px`, borderTop: V2_PANEL_HAIRLINE },
			}}
		>
			{/* As tall as the steppers, so the chips sit on their centre line. */}
			<Box sx={{ height: STEPPER.COMPACT_HEIGHT, display: "flex", alignItems: "center" }}>
				<DayToggleGrid
					size="xsmall"
					touchSize="small"
					shape="circle"
					groups={[
						{
							key: row.id,
							cells: chips.map(({ day, label, name }) => {
								const held = heldElsewhere.includes(day);
								const selected = row.days.includes(day);
								const overlap = held && selected;
								return {
									key: day,
									label,
									selected,
									disabled,
									ariaLabel: overlap
										? t("schedule.template.editor.targets.dayOverlapNamed", { day: name })
										: held
											? t("schedule.template.editor.day.held", { day: name })
											: name,
									marker: overlap ? overlapMarker : undefined,
									dataTestId: `location-staffing-target-day-${row.id}-${day}`,
								};
							}),
						},
					]}
					onToggle={(cell) => {
						const day = WEEKDAYS.find((weekday) => weekday === cell.key);
						if (!day) throw new Error(`Unknown weekday chip ${cell.key}`);
						onUpdateRow(row.id, { days: toggleWeekday(row.days, day) });
					}}
				/>
			</Box>
			<Box sx={{ display: "flex", alignItems: "flex-end", gap: `${S.AMOUNT_GAP_X}px` }}>
				<StaffingFieldV2 label={t("schedule.template.target")} labelFor={null}>
					<SystemStepper
						size="compact"
						value={row.amount}
						onChange={(amount) => onUpdateRow(row.id, { amount })}
						min={0}
						max={V2_STAFF_COUNT_MAX}
						label={t("schedule.template.target")}
						disabled={disabled}
						dataTestId={`location-staffing-target-amount-${row.id}`}
					/>
				</StaffingFieldV2>
				<StaffingFieldV2 label={t("schedule.template.required")} labelFor={null}>
					<SystemStepper
						size="compact"
						value={row.amountRequired}
						onChange={(amountRequired) => onUpdateRow(row.id, { amountRequired })}
						min={0}
						max={row.amount}
						label={t("schedule.template.required")}
						disabled={disabled}
						dataTestId={`location-staffing-target-required-${row.id}`}
					/>
				</StaffingFieldV2>
				<SystemButton
					iconButton
					buttonVariant="borderless"
					aria-label={t("schedule.template.v3.editor.removeRow")}
					tooltip={hasOccupiedDays ? t("schedule.template.cannot.delete.target.with.shifts") : undefined}
					disabled={cannotRemove || disabled}
					onClick={(e) => {
						e.stopPropagation();
						onRemoveRow(row.id);
					}}
					data-testid={`location-staffing-target-remove-button-${row.id}`}
				>
					<TrashLineIcon fontSize="small" />
				</SystemButton>
			</Box>
		</Box>
	);
};
